import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { useSalesFormatter } from "./utils/formatters";
import {
  getBrands,
  getDivisions,
  getDoctors,
  getMonths,
  getSalesOfficers,
  getStockists,
  getTerritoryPerformance,
} from "../../services/brandDashboardApi";
import "./BrandTerritoryDashboard.css";
import DashboardPanelSwitcher from "../Common/DashboardPanelSwitcher";

const PAGE_SIZE = 10;
const DRILLDOWN_PAGE_SIZE = 10;
const DOCTOR_CHART_COLORS = [
  "#5b84b8",
  "#f39c35",
  "#df5b5b",
  "#7ab9b5",
  "#60a857",
  "#e3c542",
  "#5b89c1",
  "#ee8a27",
  "#9ac7c5",
  "#c5d3eb",
];

function formatLakhs(value) {
  const lakhs = Number(value || 0) / 100000;
  return `₹ ${lakhs.toFixed(2)} L`;
}

function formatInteger(value) {
  return new Intl.NumberFormat("en-IN").format(Number(value || 0));
}

function formatPercent(value, showSign = false) {
  const prefix = showSign && Number(value) > 0 ? "+" : "";
  return `${prefix}${Number(value).toFixed(Number.isInteger(value) ? 0 : 2)}%`;
}

function getAchievementTone(value) {
  const numeric = Number(value);
  if (numeric < 80) return "tone-negative";
  if (numeric <= 90) return "tone-warning";
  return "tone-positive";
}

function getGrowthTone(value) {
  const numeric = Number(value);
  if (numeric > 0) return "tone-positive";   // ✅ GREEN
  if (numeric < 0) return "tone-negative";   // ✅ RED
  return "tone-neutral"; 
}

function sortRows(rows, sortConfig, accessors = {}) {
  if (!sortConfig?.key) return rows;

  const { key, direction, type = "text" } = sortConfig;
  const accessor = accessors[key] || ((item) => item[key]);
  const factor = direction === "asc" ? 1 : -1;

  return [...rows].sort((a, b) => {
    const aValue = accessor(a);
    const bValue = accessor(b);

    if (type === "number") {
      return (Number(aValue) - Number(bValue)) * factor;
    }

    return String(aValue).localeCompare(String(bValue), undefined, { sensitivity: "base" }) * factor;
  });
}

function useSortableData(initialConfig = { key: "", direction: "asc", type: "text" }) {
  const [sortConfig, setSortConfig] = useState(initialConfig);

  function toggleSort(key, type = "text") {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          type,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return {
        key,
        type,
        direction: "asc",
      };
    });
  }

  return { sortConfig, toggleSort, setSortConfig };
}

function Loader({ text = "Loading..." }) {
  return <div className="section-state">{text}</div>;
}

function ErrorState({ message }) {
  return <div className="section-state section-state--error">{message}</div>;
}

function MetricCard({ label, value, tone = "", compact = false, breakdown, brand }) {
  return (
    <div
      className={`metric-card ${compact ? "metric-card--compact" : ""}`}
      style={{ position: "relative" }}
    >
      <div className="metric-card__label">{label}</div>

      {/* 🔥 BRAND NAME */}
      {brand && <div className="metric-subtext">{brand}</div>}

      <div className={`metric-card__value ${tone}`}>{value}</div>

      {/* 🔥 CREDIT DETAILS (ALWAYS VISIBLE) */}
      {breakdown && (
        <div className="metric-breakdown">
          <div className="tooltip-title">Credit Details</div>

          {breakdown.map((item, i) => (
            <div key={i} className="tooltip-row">
              <span>{item.label}</span>
              <span>{item.value}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SortIcon({ active, direction }) {
  return (
    <span className="sort-icons" aria-hidden="true">
      <span className={`sort-icons__up ${active && direction === "asc" ? "active" : ""}`}>▲</span>
      <span className={`sort-icons__down ${active && direction === "desc" ? "active" : ""}`}>▼</span>
    </span>
  );
}

function SortableHeader({ label, sortKey, sortType = "text", sortConfig, onSort, className = "" }) {
  const active = sortConfig?.key === sortKey;

  return (
    <th className={`sortable-th ${className}`}>
      <button type="button" className="sortable-trigger" onClick={() => onSort(sortKey, sortType)}>
        <span>{label}</span>
        <SortIcon active={active} direction={sortConfig?.direction} />
      </button>
    </th>
  );
}

function Pagination({ currentPage, totalPages, onPageChange }) {
  if (totalPages <= 1) return null;

  const pages = [];
  const start = Math.max(1, currentPage - 2);
  const end = Math.min(totalPages, currentPage + 2);

  for (let page = start; page <= end; page += 1) {
    pages.push(page);
  }

  return (
    <div className="pagination">
      <button className="page-btn" onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}>
        Prev
      </button>

      {start > 1 && (
        <>
          <button className="page-btn" onClick={() => onPageChange(1)}>
            1
          </button>
          {start > 2 && <span className="page-ellipsis">...</span>}
        </>
      )}

      {pages.map((page) => (
        <button
          key={page}
          className={`page-btn ${currentPage === page ? "active" : ""}`}
          onClick={() => onPageChange(page)}
        >
          {page}
        </button>
      ))}

      {end < totalPages && (
        <>
          {end < totalPages - 1 && <span className="page-ellipsis">...</span>}
          <button className="page-btn" onClick={() => onPageChange(totalPages)}>
            {totalPages}
          </button>
        </>
      )}

      <button
        className="page-btn"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      >
        Next
      </button>
    </div>
  );
}

function CustomDropdown({
  label,
  placeholder,
  items = [],
  selected,
  onSelect,
  loading,
  error,
  disabled,
  renderItem,
  getButtonText,
  searchable = false,
}) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const containerRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
        setSearchTerm("");
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (disabled) {
      setOpen(false);
      setSearchTerm("");
    }
  }, [disabled]);

  const filteredItems = searchable && searchTerm.trim()
    ? (items || []).filter((item) => {
        const text = (item.name || item.label || item.value || "").toLowerCase();
        return text.includes(searchTerm.trim().toLowerCase());
      })
    : (items || []);

  return (
    <div className="dropdown-field" ref={containerRef}>
      <label className="field-label">{label}</label>

      <button
        type="button"
        className={`dropdown-trigger ${open ? "is-open" : ""}`}
        disabled={disabled}
        onClick={() => {
          const nextOpen = !open;
          setOpen(nextOpen);
          if (!nextOpen) setSearchTerm("");
          if (nextOpen && searchable) {
            setTimeout(() => searchRef.current?.focus(), 50);
          }
        }}
      >
        <span className={selected ? "trigger-text" : "trigger-placeholder"}>
          {selected ? getButtonText(selected) : placeholder}
        </span>

        <span className="trigger-arrow">▾</span>
      </button>

      {open && (
        <div className="dropdown-menu">
          {searchable && !loading && (
            <div className="dropdown-search-wrapper">
              <input
                ref={searchRef}
                type="text"
                className="dropdown-search-input"
                placeholder="Type to search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}

          {loading && (
            <div className="dropdown-state">Loading...</div>
          )}

          {!loading && error && (
            <div className="dropdown-state dropdown-state--error">
              {error}
            </div>
          )}

          {!loading && !error && (!filteredItems || filteredItems.length === 0) && (
            <div className="dropdown-state">{searchTerm ? "No matches found" : "No options available"}</div>
          )}

          {!loading &&
            !error &&
            filteredItems.map((item, index) => (
            <button
              type="button"
              key={item?.id || item?.name || index}
              className={`dropdown-option ${
              selected?.id === item?.id ? "selected" : ""
            }`}
            onClick={() => {
              onSelect(item);
              setOpen(false);
              setSearchTerm("");
            }}
          >
            {renderItem(item)}
          </button>
      ))}

        </div>
      )}
    </div>
  );
}

function FiltersBar({
  months,
  selectedMonth,
  onMonthChange,
  loadingMonths,
  monthsError,
  divisions,
  selectedDivision,
  onDivisionChange,
  loadingDivisions,
  divisionsError,
  brands,
  selectedBrand,
  onBrandChange,
  loadingBrands,
  brandsError,
  onApply,
  applyDisabled,
}) {
  const renderRichOption = (item) => (
    <div className="rich-option">
      <div className="rich-option__header">
        <span className="rich-option__title">{item.name}</span>
        <span className="rich-option__sales">Sales: {formatLakhs(item.salesLakh)}</span>
      </div>
      <div className="rich-option__footer">
        <span className="rich-option__employees">Employees: {formatInteger(item.employees)}</span>
        <span className={`rich-option__achievement ${getAchievementTone(item.achievement)}`}>
          Achievement: {item.achievement}%
        </span>
      </div>
    </div>
  );

  return (
    <div className="filter-card">
      <div className="filter-grid">
        <CustomDropdown
          label="Month & Year"
          placeholder="Select month"
          items={months}
          selected={selectedMonth}
          onSelect={onMonthChange}
          loading={loadingMonths}
          error={monthsError}
          disabled={false}
          getButtonText={(item) => item.label}
          renderItem={(item) => <div className="simple-option">{item.label}</div>}
        />

        <CustomDropdown
          label="Division"
          placeholder="Select division"
          items={divisions}
          selected={selectedDivision}
          onSelect={onDivisionChange}
          loading={loadingDivisions}
          error={divisionsError}
          disabled={!selectedMonth}
          getButtonText={(item) => item.name}
          renderItem={renderRichOption}
          searchable
        />

        <CustomDropdown
          label="Brand"
          placeholder="Select brand"
          items={brands}
          selected={selectedBrand}
          onSelect={onBrandChange}
          loading={loadingBrands}
          error={brandsError}
          disabled={!selectedDivision}
          getButtonText={(item) => item.name}
          renderItem={renderRichOption}
          searchable
        />

        <div className="apply-wrapper">
          <label className="field-label field-label--hidden">Apply</label>
          <button className="apply-btn" disabled={applyDisabled} onClick={onApply}>
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}

function TerritorySummaryCards({ summary }) {
  const cards = [
    { label: "Total Territories", value: formatInteger(summary.totalTerritories) },
    { label: "Good Performing", value: formatInteger(summary.goodPerforming), tone: "tone-positive" },
    { label: "Deficit Territories", value: formatInteger(summary.deficitTerritories), tone: "tone-negative" },
    { label: "Total Sales Officers", value: formatInteger(summary.totalSalesOfficers) },
    { label: "Total Stockists", value: formatInteger(summary.totalStockists) },
  ];

  return (
    <div className="summary-card-grid">
      {cards.map((card) => (
        <MetricCard key={card.label} compact label={card.label} value={card.value} tone={card.tone || ""} />
      ))}
    </div>
  );
}

function TerritoryTable({
  salesUnit,
  setSalesUnit,
  month,
  division,
  brand,
  summary,
  territories,
  loading,
  error,
  search,
  onSearchChange,
  currentPage,
  totalPages,
  onPageChange,
  onDrillDown,
  sortConfig,
  onSort,
}) {
  const formatSales = useSalesFormatter(salesUnit);
  const unitLabel = {
    absolute: "₹",
    lakh: "L",
    crore: "Cr",
  };

  if (loading) {
    return (
      <div className="panel-card">
        <Loader text="Loading territory performance..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="panel-card">
        <ErrorState message={error} />
      </div>
    );
  }

  return (
    <div className="panel-card">
      <TerritorySummaryCards summary={summary} />

      {/* ✅ FIXED HEADER */}
      <div
        className="panel-head"
        style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
      >
        <div>
          <div className="breadcrumbs">
            Showing metrics for: {month.label} → {division.name} → {brand.name}
          </div>
          <h2 className="panel-title">
            Territory Performance - {brand.name} ({month.label})
          </h2>
        </div>

        {/* ✅ DROPDOWN */}
        <div className="sales-toggle-container">
          <button
            className={salesUnit === "absolute" ? "toggle-btn active" : "toggle-btn"}
            onClick={() => setSalesUnit("absolute")}
          >
            Abs
          </button>

          <button
            className={salesUnit === "lakh" ? "toggle-btn active" : "toggle-btn"}
            onClick={() => setSalesUnit("lakh")}
          >
            Lakh
          </button>

          <button
            className={salesUnit === "crore" ? "toggle-btn active" : "toggle-btn"}
            onClick={() => setSalesUnit("crore")}
          >
            Cr
          </button>
        </div>
      </div>

      {territories.length === 0 ? (
        <div className="section-state">No territories found.</div>
      ) : (
        <>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Territory</th>
                  <SortableHeader
                    label="Achievement %"
                    sortKey="achievement"
                    sortType="number"
                    sortConfig={sortConfig}
                    onSort={onSort}
                  />
                  <SortableHeader
                    // label="Sales (₹)"
                    label={`Sales (${unitLabel[salesUnit]})`}
                    sortKey="sales"
                    sortType="number"
                    sortConfig={sortConfig}
                    onSort={onSort}
                  />
                  <SortableHeader
                    label="Sales Officers"
                    sortKey="salesOfficers"
                    sortType="number"
                    sortConfig={sortConfig}
                    onSort={onSort}
                  />
                  <SortableHeader
                    label="Stockists"
                    sortKey="stockists"
                    sortType="number"
                    sortConfig={sortConfig}
                    onSort={onSort}
                  />
                  <SortableHeader
                    label="Growth Rate %"
                    sortKey="growth"
                    sortType="number"
                    sortConfig={sortConfig}
                    onSort={onSort}
                  />
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {territories.map((item) => (
                  <tr key={item.id}>
                    <td>{item.name}</td>

                    <td>
                      <span className={`pill ${getAchievementTone(item.achievement)}`}>
                        {formatPercent(item.achievement)}
                      </span>
                    </td>

                    <td>
                      <div className="cell-main"> {formatSales(item.sales)}</div>
                      <div className="cell-sub">
                        Target:{formatSales(item.target)} 
                      </div>
                    </td>

                    <td>{formatInteger(item.salesOfficers)}</td>
                    <td>{formatInteger(item.stockists)}</td>

                    <td>
                      <span className={`pill ${getGrowthTone(item.growth)}`}>
                        {formatPercent(item.growth, true)}
                      </span>
                    </td>

                    <td>
                      <button
                        className="secondary-btn"
                        onClick={() => onDrillDown(item)}
                      >
                        Drill Down
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
          />
        </>
      )}
    </div>
  );
}

function CoverageProgress({ value }) {
  const safeValue = Math.max(0, Math.min(100, Number(value || 0)));
  return (
    <div className="coverage-progress">
      <div className="coverage-progress__bar">
        <div className={`coverage-progress__fill ${getAchievementTone(safeValue)}`} style={{ width: `${safeValue}%` }} />
      </div>
      <div className="coverage-progress__meta">
        <span className={`metric-text ${getAchievementTone(safeValue)}`}>{formatPercent(safeValue)}</span>
        <span className="coverage-progress__scale">/ 100%</span>
      </div>
    </div>
  );
}

function SalesOfficersTab({ state, currentPage, onPageChange }) {
  
  // const [search, setSearch] = useState("");
  const { sortConfig, toggleSort } = useSortableData({
    key: "coverage",
    direction: "desc",
    type: "number",
  });

 

  const filteredItems = useMemo(() => {
  return sortRows(state.items, sortConfig);
}, [state.items, sortConfig]);

  const totalPages = Math.max(
  1,
  Math.ceil((state.total || 0) / DRILLDOWN_PAGE_SIZE)
);
  const pageItems = filteredItems;
  

  if (state.loading) return <Loader text="Loading sales officers coverage..." />;
  if (state.error) return <ErrorState message={state.error} />;

  return (
    <div className="tab-panel">
      <div className="metric-grid">
        <MetricCard label="Total Sales Officers" value={formatInteger(state.metrics.totalSalesOfficers)} />
        <MetricCard label="Avg. Doctors / Employee" value={state.metrics.avgDoctorsPerEmployee || 0} />
        <MetricCard
          label="Avg. Doctors Covered / Employee"
          value={state.metrics.avgDoctorsCoveredPerEmployee || 0}
          tone="tone-positive"
        />
        <MetricCard
          label="Average Coverage"
          value={formatPercent(state.metrics.averageCoverage || 0)}
          tone="tone-positive"
        />
      </div>

      <div className="panel-card panel-card--inner">
        <div className="panel-head">
          <h3 className="panel-subtitle">Sales Officers Coverage Details</h3>
          {/* <div className="toolbar-search">
            <input
              type="text"
              placeholder="Search by name, code, or designation..."
              value={search}
              onChange={(event) => {
                onPageChange(1);
                setSearch(event.target.value);
              }}
            />
          </div> */}
        </div>

        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <SortableHeader
                  label="Employee Code"
                  sortKey="employeeCode"
                  sortType="text"
                  sortConfig={sortConfig}
                  onSort={toggleSort}
                />
                <SortableHeader
                  label="Employee Name"
                  sortKey="employeeName"
                  sortType="text"
                  sortConfig={sortConfig}
                  onSort={toggleSort}
                />
                <SortableHeader
                  label="Designation"
                  sortKey="designation"
                  sortType="text"
                  sortConfig={sortConfig}
                  onSort={toggleSort}
                />
                <SortableHeader
                  label="Total Doctors"
                  sortKey="totalDoctors"
                  sortType="number"
                  sortConfig={sortConfig}
                  onSort={toggleSort}
                />
                <SortableHeader
                  label="Covered Doctors"
                  sortKey="coveredDoctors"
                  sortType="number"
                  sortConfig={sortConfig}
                  onSort={toggleSort}
                />
                <SortableHeader
                  label="Coverage %"
                  sortKey="coverage"
                  sortType="number"
                  sortConfig={sortConfig}
                  onSort={toggleSort}
                />
              </tr>
            </thead>
            <tbody>
              {pageItems.map((item) => (
                <tr key={item.employeeCode}>
                  <td>{item.employeeCode}</td>
                  <td>{item.employeeName}</td>
                  <td>{item.designation}</td>
                  <td>{formatInteger(item.totalDoctors)}</td>
                  <td>{formatInteger(item.coveredDoctors)}</td>
                  <td>
                    <CoverageProgress value={item.coverage} />
                  </td>
                </tr>
              ))}
              {!pageItems.length && (
                <tr>
                  <td colSpan="6" className="empty-cell">
                    No sales officer records found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <Pagination
  currentPage={currentPage}
  totalPages={totalPages}
  onPageChange={onPageChange}
/>
      </div>
    </div>
  );
}

// function DoctorDistributionChart({ distribution }) {
//   // const total = distribution.reduce((sum, item) => sum + item.value, 0);
//   const chartData = (data.distribution || []).map(item => ({
//     name: item.speciality || "Unknown",
//     value: item.count
//   }));
//   const radius = 110;
//   const innerRadius = 58;
//   const size = 260;
//   const center = size / 2;

//   function polarToCartesian(cx, cy, r, angleInDegrees) {
//     const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180;
//     return {
//       x: cx + r * Math.cos(angleInRadians),
//       y: cy + r * Math.sin(angleInRadians),
//     };
//   }

//   function describeArc(startAngle, endAngle) {
//     const startOuter = polarToCartesian(center, center, radius, endAngle);
//     const endOuter = polarToCartesian(center, center, radius, startAngle);
//     const startInner = polarToCartesian(center, center, innerRadius, endAngle);
//     const endInner = polarToCartesian(center, center, innerRadius, startAngle);
//     const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0;

//     return [
//       `M ${startOuter.x} ${startOuter.y}`,
//       `A ${radius} ${radius} 0 ${largeArcFlag} 0 ${endOuter.x} ${endOuter.y}`,
//       `L ${endInner.x} ${endInner.y}`,
//       `A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 1 ${startInner.x} ${startInner.y}`,
//       "Z",
//     ].join(" ");
//   }

//   let runningAngle = 0;
//   const slices = distribution.map((item, index) => {
//     const angle = total === 0 ? 0 : (item.value / total) * 360;
//     const startAngle = runningAngle;
//     const endAngle = runningAngle + angle;
//     runningAngle += angle;

//     return {
//       ...item,
//       percentage: total === 0 ? 0 : Number(((item.value / total) * 100).toFixed(2)),
//       color: DOCTOR_CHART_COLORS[index % DOCTOR_CHART_COLORS.length],
//       path: describeArc(startAngle, endAngle),
//     };
//   });

//   return (
//     <div className="doctor-chart-card">
//       <div className="panel-subtitle panel-subtitle--small">Specialty Distribution</div>
//       <div className="doctor-chart-layout">
//         <div className="doctor-chart-svg-wrap">
//           <svg viewBox={`0 0 ${size} ${size}`} className="doctor-chart-svg" role="img" aria-label="Doctor specialty distribution pie chart">
//             <circle cx={center} cy={center} r={radius} fill="#f6f7fb" />
//             {slices.map((slice) => (
//               <path key={slice.name} d={slice.path} fill={slice.color} stroke="#ffffff" strokeWidth="4" />
//             ))}
//             <circle cx={center} cy={center} r={innerRadius - 2} fill="#ffffff" />
//             <text x="50%" y="48%" textAnchor="middle" className="chart-center-text">
//               {total}
//             </text>
//             <text x="50%" y="58%" textAnchor="middle" className="chart-center-subtext">
//               Doctors
//             </text>
//           </svg>
//         </div>

//         <div className="doctor-chart-legend">
//           {slices.map((slice) => (
//             <div className="legend-item" key={slice.name}>
//               <span className="legend-item__dot" style={{ backgroundColor: slice.color }} />
//               <div className="legend-item__text">
//                 <div className="legend-item__name">{slice.name}</div>
//                 <div className="legend-item__value">{slice.percentage}%</div>
//               </div>
//             </div>
//           ))}
//         </div>
//       </div>
//     </div>
//   );
// }
function DoctorDistributionChart({ distribution = [] }) {
  const total = distribution.reduce((sum, item) => sum + (item.value || 0), 0);

  const radius = 110;
  const innerRadius = 58;
  const size = 260;
  const center = size / 2;

  function polarToCartesian(cx, cy, r, angleInDegrees) {
    const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180;
    return {
      x: cx + r * Math.cos(angleInRadians),
      y: cy + r * Math.sin(angleInRadians),
    };
  }

  function describeArc(startAngle, endAngle) {
    const startOuter = polarToCartesian(center, center, radius, endAngle);
    const endOuter = polarToCartesian(center, center, radius, startAngle);
    const startInner = polarToCartesian(center, center, innerRadius, endAngle);
    const endInner = polarToCartesian(center, center, innerRadius, startAngle);
    const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0;

    return [
      `M ${startOuter.x} ${startOuter.y}`,
      `A ${radius} ${radius} 0 ${largeArcFlag} 0 ${endOuter.x} ${endOuter.y}`,
      `L ${endInner.x} ${endInner.y}`,
      `A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 1 ${startInner.x} ${startInner.y}`,
      "Z",
    ].join(" ");
  }

  let runningAngle = 0;

  const slices = distribution.map((item, index) => {
    const value = item.value || 0;
    const angle = total === 0 ? 0 : (value / total) * 360;

    const startAngle = runningAngle;
    const endAngle = runningAngle + angle;
    runningAngle += angle;

    return {
      ...item,
      percentage: total === 0 ? 0 : Number(((value / total) * 100).toFixed(2)),
      color: DOCTOR_CHART_COLORS[index % DOCTOR_CHART_COLORS.length],
      path: describeArc(startAngle, endAngle),
    };
  });

  return (
    <div className="doctor-chart-card">
      <div className="panel-subtitle panel-subtitle--small">
        Specialty Distribution
      </div>

      <div className="doctor-chart-layout">
        <div className="doctor-chart-svg-wrap">
          <svg viewBox={`0 0 ${size} ${size}`} className="doctor-chart-svg">
            <circle cx={center} cy={center} r={radius} fill="#f6f7fb" />

            {slices.map((slice, index) => (
              <path
                key={`${slice.name}-${index}`}
                d={slice.path}
                fill={slice.color}
                stroke="#ffffff"
                strokeWidth="4"
              />
            ))}

            <circle cx={center} cy={center} r={innerRadius - 2} fill="#ffffff" />

            <text x="50%" y="48%" textAnchor="middle" className="chart-center-text">
              {total}
            </text>
            <text x="50%" y="58%" textAnchor="middle" className="chart-center-subtext">
              Doctors
            </text>
          </svg>
        </div>

        <div className="doctor-chart-legend">
          {slices.map((slice, index) => (
            <div className="legend-item" key={`${slice.name}-${index}`}>
              <span
                className="legend-item__dot"
                style={{ backgroundColor: slice.color }}
              />
              <div className="legend-item__text">
                <div className="legend-item__name">{slice.name}</div>
                <div className="legend-item__value">{slice.percentage}%</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


function DoctorDataTab({ 
  state, 
  filters, 
  onFiltersChange, 
  currentPage, 
  onPageChange,
  status,
  onStatusChange
}) {
  const { sortConfig, toggleSort } = useSortableData({
    key: "doctorName",
    direction: "asc",
    type: "text",
  });

  // 🔥 Reset page on filters change
  useEffect(() => {
    onPageChange(1);
  }, [filters.specialty, filters.potential, filters.employeeId]);

  // 🔥 Reset page on status change (NEW | ALL)
  useEffect(() => {
    onPageChange(1);
  }, [status]);

  const filteredItems = useMemo(() => {
    return sortRows(state.items, sortConfig, {
      visitFrequency: (item) => item.visitFrequency || 0,
      potential: (item) => item.potentialValue || 0,
    });
  }, [state.items, sortConfig]);

  const chartData = (state.metrics?.distribution || []).map((item) => ({
    name: item.speciality || "Unknown",
    value: item.count || 0,
  }));

  const totalPages = Math.max(
    1,
    Math.ceil((state.total || 0) / DRILLDOWN_PAGE_SIZE)
  );

  const pageItems = filteredItems;

  useEffect(() => {
    if (currentPage > totalPages) onPageChange(1);
  }, [currentPage, totalPages]);

  if (state.loading) return <Loader text="Loading doctor data..." />;
  if (state.error) return <ErrorState message={state.error} />;

  return (
    <div className="tab-panel">
      <div className="doctor-grid">
        <DoctorDistributionChart distribution={chartData} />

        <div className="panel-card panel-card--inner doctor-table-card">
          <div className="panel-head panel-head--stack doctor-panel-head">

            {/* 🔥 LEFT SIDE */}
            <div>
              <h3 className="panel-subtitle">Key Doctors in Territory</h3>

              <div className="summary-text">
                Total Visited Doctors:{" "}
                <strong className="summary-accent">
                  {formatInteger(state.metrics.totalVisitedDoctors)}
                </strong>
              </div>

              {/* 🔥 NEW CHIP UI */}
              <div className="chip-row" style={{ marginTop: "10px" }}>
                <button
                  className={`chip ${status === "all" ? "active" : ""}`}
                  onClick={() => onStatusChange("all")}
                >
                  All 
                </button>

                <button
                  className={`chip ${status === "new" ? "active" : ""}`}
                  onClick={() => onStatusChange("new")}
                >
                  New 
                </button>
              </div>
            </div>


          </div>

          {/* 🔥 TABLE */}
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <SortableHeader
                    label="Doctor Name"
                    sortKey="doctorName"
                    sortType="text"
                    sortConfig={sortConfig}
                    onSort={toggleSort}
                  />
                  <SortableHeader
                    label="Specialty"
                    sortKey="specialty"
                    sortType="text"
                    sortConfig={sortConfig}
                    onSort={toggleSort}
                  />
                  <SortableHeader
                    label="Visit Frequency"
                    sortKey="visitFrequency"
                    sortType="number"
                    sortConfig={sortConfig}
                    onSort={toggleSort}
                  />
                  <SortableHeader
                    label="Potential"
                    sortKey="potential"
                    sortType="number"
                    sortConfig={sortConfig}
                    onSort={toggleSort}
                  />
                  <SortableHeader
                    label="Assigned Employee"
                    sortKey="assignedEmployee"
                    sortType="text"
                    sortConfig={sortConfig}
                    onSort={toggleSort}
                  />
                </tr>
              </thead>

              <tbody>
                {pageItems.map((item) => (
                  <tr key={item.id}>
                    <td>{item.doctorName}</td>
                    <td>{item.specialty}</td>
                    <td>{item.visitFrequencyLabel}</td>
                    <td>{item.potential}</td>
                    <td>{item.assignedEmployee}</td>
                  </tr>
                ))}

                {!pageItems.length && (
                  <tr>
                    <td colSpan="5" className="empty-cell">
                      No doctor records found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
          />
        </div>
      </div>
    </div>
  );
}

function StockistDetailsTab({ 
    state,
    status,
    onStatusChange,
    currentPage,
    onPageChange,
    formatSales,
    timeFilter,
    setTimeFilter,
    brand 
  }) {
  
  // const [search, setSearch] = useState("");
  const { sortConfig, toggleSort } = useSortableData({
    key: "mtdSales",
    direction: "desc",
    type: "number",
  });

  useEffect(() => {
    onPageChange(1);
  }, [status]);

  const filteredItems = useMemo(() => {
  return sortRows(state.items, sortConfig);
}, [state.items, sortConfig]);

  const totalPages = Math.max(
  1,
  Math.ceil((state.total || 0) / DRILLDOWN_PAGE_SIZE)
   );
  const pageItems = filteredItems;



  if (state.loading) return <Loader text="Loading stockist details..." />;
  if (state.error) return <ErrorState message={state.error} />;
  return (
  <div className="tab-panel">
    {/* 🔥 METRICS */}
    <div className="metric-grid">
      <MetricCard
        label="Total Stockists"
        value={formatInteger(state.metrics.totalStockists)}
      />

      <MetricCard
        label={`Total ${timeFilter} Sales`}
        value={formatSales(state.metrics.totalSales)}
      />

      <MetricCard
        label="Average Growth"
        value={formatPercent(state.metrics.averageGrowth || 0, true)}
        tone={getGrowthTone(state.metrics.averageGrowth)}   // ✅ FIX
      />

      <MetricCard
        label="Positive Growth Stockists"
        value={`${formatInteger(
          state.metrics.positiveGrowthStockists
        )} / ${formatInteger(state.metrics.totalStockists)}`}
        tone="tone-positive"
      />
      
      <MetricCard
        label="Brand"
        value={state.summary?.brand || "-"}   // 🔥 SHOW BRAND NAME
        breakdown={[
          { label: "Breakage", value: formatSales(state.metrics.breakage || 0) },
          { label: "Return", value: formatSales(state.metrics.return || 0) },
          { label: "Expiry", value: formatSales(state.metrics.expiry || 0) },
        ]}
      />
    </div>

    {/* 🔥 MAIN PANEL */}
    <div className="panel-card panel-card--inner">

      {/* 🔥 HEADER */}
      <div className="panel-head">

        {/* LEFT SIDE */}
        <div className="stockist-left">
          <h3 className="panel-subtitle">Stockist Details</h3>

          <div className="chip-row">
            <button
              className={`chip ${status === "all" ? "active" : ""}`}
              onClick={() => onStatusChange("all")}
            >
              All {state.counts.all || 0}
            </button>

            <button
              className={`chip ${status === "new" ? "active" : ""}`}
              onClick={() => onStatusChange("new")}
            >
              New 
            </button>

            <button
              className={`chip ${
                status === "ZeroSales" ? "active" : ""
              }`}
              onClick={() => onStatusChange("ZeroSales")}
            >
              ZeroSales 
            </button>
          </div>
        </div>

        {/* RIGHT SIDE → TOGGLE (HIDE FOR ZERO SALES) */}
{status !== "ZeroSales" && (
      <div className="toggle-container">
        <button
          className={`toggle-btn ${timeFilter === "MTD" ? "active" : ""}`}
          onClick={() => setTimeFilter("MTD")}
        >
          MTD
        </button>

        <button
          className={`toggle-btn ${timeFilter === "YTD" ? "active" : ""}`}
          onClick={() => setTimeFilter("YTD")}
        >
          YTD
        </button>
      </div>
)}

      </div>

      {/* 🔥 TABLE */}
      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <SortableHeader
                label="Stockist Name"
                sortKey="stockistName"
                sortType="text"
                sortConfig={sortConfig}
                onSort={toggleSort}
              />

              <SortableHeader
                label={`${timeFilter} Sales (₹)`}
                sortKey="sales"
                sortType="number"
                sortConfig={sortConfig}
                onSort={toggleSort}
              />

              <SortableHeader
                label={`Last Year ${timeFilter} Sales (₹)`}
                sortKey="lastYearSales"
                sortType="number"
                sortConfig={sortConfig}
                onSort={toggleSort}
              />

              <SortableHeader
                label="Growth %"
                sortKey="growth"
                sortType="number"
                sortConfig={sortConfig}
                onSort={toggleSort}
              />
            </tr>
          </thead>

          <tbody>
            {pageItems.map((item) => (
              <tr key={item.id}>
                <td>{item.stockistName}</td>

                {/* 🔥 FIXED DATA */}
                <td>{formatSales(item.sales)}</td>
                <td>{formatSales(item.lastYearSales)}</td>

                <td>
                  <span className={`pill ${getGrowthTone(item.growth)}`}>
                    {formatPercent(item.growth, true)}
                  </span>
                </td>
              </tr>
            ))}

            {!pageItems.length && (
              <tr>
                <td colSpan="4" className="empty-cell">
                  No stockists found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* 🔥 PAGINATION */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={onPageChange}
      />
    </div>
  </div>
);
}

function TerritoryDrilldownModal({ month, division, brand, territory, onClose,formatSales }) {
  const [page, setPage] = useState(1);
  const [activeTab, setActiveTab] = useState("sales-officers");
  const [stockistPage, setStockistPage] = useState(1);
  const [salesPage, setSalesPage] = useState(1);
  const [timeFilter, setTimeFilter] = useState("MTD");
  const [doctorStatus, setDoctorStatus] = useState("all");
  useEffect(() => {
  if (activeTab === "stockist-details") {
    setStockistPage(1);
  }
}, [activeTab]);
  const [salesState, setSalesState] = useState({
    loading: true,
    error: "",
    metrics: {},
    items: [],
    counts: { all: 0, new: 0 } ,
  });

  const [doctorState, setDoctorState] = useState({
    loading: false,
    error: "",
    metrics: { totalVisitedDoctors: 0, distribution: [] },
    filterOptions: { specialties: [], potentials: [], employees: [] },
    items: [],
    total: 0,
    counts: { all: 0, new: 0 } 
  });

  const [stockistState, setStockistState] = useState({
    loading: false,
    error: "",
    metrics: { totalStockists: 0, totalMtdSales: 0, averageGrowth: 0, positiveGrowthStockists: 0 },
    counts: { all: 0, new: 0, ZeroSales: 0 },
    items: [],
  });

  const [doctorFilters, setDoctorFilters] = useState({
    specialty: "",
    potential: "",
    employeeId: "",
  });
  const [stockistStatus, setStockistStatus] = useState("all");

  useEffect(() => {
    setStockistPage(1);
  }, [timeFilter]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  useEffect(() => {
    async function loadSalesOfficers() {
      setSalesState((prev) => ({ ...prev, loading: true, error: "" }));
      try {
        // const data = await getSalesOfficers({ territoryId: territory.id, month: month.id, brandId: brand.id });
        const data = await getSalesOfficers({
            month: month.id,
            division: division.name,
            brand: brand.name,
            territory: territory.name,
            page: salesPage,
            page_size: PAGE_SIZE
          });
        setSalesState({
          loading: false,
          error: "",
          metrics: data.metrics || {},
          items: data.items || [],
          total: data.total || 0   // ✅ ADD THIS
          
        });
      } catch (error) {
        setSalesState({ loading: false, error: "Failed to load sales officers coverage.", metrics: {}, items: [] });
      }
    }

    if (activeTab === "sales-officers") {
      loadSalesOfficers();
    }
  }, [activeTab, territory.id, month.id, brand.id, salesPage]);
  const DOCTOR_CATEGORY_MAP = {
    all: "ALL",
    new: "NEW"
  };
  useEffect(() => {
  setPage(1);
}, [doctorFilters]);
  useEffect(() => {
    async function loadDoctors() {
      setDoctorState((prev) => ({ ...prev, loading: true, error: "" }));
      try {
        const data = await getDoctors({
          month: month.id,
          division: division.name,
          brand: brand.name,
          territory: territory.name,

          specialty: doctorFilters.specialty,
          potential: doctorFilters.potential,
          employeeId: doctorFilters.employeeId,
          category: DOCTOR_CATEGORY_MAP[doctorStatus],
          page: page,
          page_size: 10
        });

        setDoctorState({
          loading: false,
          error: "",

          metrics: {
            totalVisitedDoctors: data.summary?.total_doctors || 0,
            distribution: data.speciality_distribution || []
          },

          filterOptions: {
            specialties: [],
            potentials: [],
            employees: []
          },

          items: (data.doctors || []).map((d, index) => ({
            id: index,

            doctorName: d.doctor_name || "Unknown",
            specialty: d.speciality || "Unknown",
            visitFrequency: d.visit_frequency || 0,
            // visitFrequencyLabel: d.visit_frequency || 0,
            visitFrequencyLabel: `${d.visit_frequency || 0} VISIT`,
            potential: d.potential || "Unknown",
            assignedEmployee: d.employee_name || "Unknown",
              
            // needed for sorting
            potentialValue: d.potential || 0
          })),
          total: data.total || 0,
        });
      } catch (error) {
        setDoctorState({
          loading: false,
          error: "Failed to load doctor data.",
          metrics: { totalVisitedDoctors: 0, distribution: [] },
          filterOptions: { specialties: [], potentials: [], employees: [] },
          items: [],
          counts: { all: 0, new: 0 },
        });
      }
    }

    if (activeTab === "doctor-data") {
      loadDoctors();
    }
  // }, [activeTab, territory.id, month.id, brand.id, doctorFilters]);
  // }, [activeTab, territory.id, month.id, brand.id, division.name, doctorFilters]);
  }, [
  activeTab,
  territory.id,
  month.id,
  brand.id,
  division.name,
  doctorFilters.specialty,
  doctorFilters.potential,
  doctorFilters.employeeId,
  doctorStatus,
  page
]);
const CATEGORY_MAP = {
  all: "ALL",
  new: "NEW",
  ZeroSales: "ZERO" // 🔥 FIX (backend expects ZERO)
};
  useEffect(() => {
    async function loadStockists() {
      setStockistState((prev) => ({ ...prev, loading: true, error: "" }));
      try {
        const data = await getStockists({
          month: month.id,
          division: division.name,
          brand: brand.name,
          territory: territory.name,
          page: stockistPage,
          page_size: PAGE_SIZE,
          // category: stockistStatus.toUpperCase(),
          category: CATEGORY_MAP[stockistStatus],
          time_filter: timeFilter
        });
        setStockistState({
          loading: false,
          error: "",
          summary: data.summary || {},   // 🔥 ADD THIS
          metrics: data.metrics || {},

          counts: {
            all: data.total || 0,
            new: 0,
            discontinued: 0
          },
          items: data.items || [],
          total: data.total || 0   // ✅ ADD THIS
        });
      } catch (error) {
        setStockistState({
          loading: false,
          error: "Failed to load stockist details.",
          metrics: {},
          counts: { all: 0, new: 0, discontinued: 0 },
          items: [],
        });
      }
    }

    if (activeTab === "stockist-details") {
      loadStockists();
    }
  // }, [activeTab, territory.id, month.id, brand.id, stockistStatus, stockistPage,timeFilter]);
  },[
    activeTab,
    territory?.id,
    month?.id,
    brand?.id,
    division?.name,
    stockistStatus,
    stockistPage,
    timeFilter || "MTD"
  ]);

  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-shell" onClick={(event) => event.stopPropagation()}>
        <div className="modal-header">
          <div>
            <div className="eyebrow">AI Brand Manager</div>
            <h2 className="drilldown-title">
              {territory.name} - Detailed Analysis <span>({month.label})</span>
            </h2>
            <div className="breadcrumbs">
              Context: {division.name} → {brand.name}
            </div>
          </div>

          <div className="modal-header-actions">
            {/* <button className="ghost-btn">Generate Pitches</button> */}
            {false && <button className="ghost-btn">Generate Pitches</button>}
            <button className="icon-btn" onClick={onClose} aria-label="Close details popup">
              ✕
            </button>
          </div>
        </div>

        <div className="tabs-row">
          <button
            className={`tab-btn ${activeTab === "sales-officers" ? "active" : ""}`}
            onClick={() => setActiveTab("sales-officers")}
          >
            Sales Officers Coverage
          </button>
          <button
            className={`tab-btn ${activeTab === "doctor-data" ? "active" : ""}`}
            onClick={() => setActiveTab("doctor-data")}
          >
            Doctor Data
          </button>
          <button
            className={`tab-btn ${activeTab === "stockist-details" ? "active" : ""}`}
            onClick={() => setActiveTab("stockist-details")}
          >
            Stockist Details
          </button>
        </div>

        <div className="modal-body">
          {activeTab === "sales-officers" && <SalesOfficersTab
                state={salesState}
                currentPage={salesPage}
                onPageChange={setSalesPage}
              />}
          {/* {activeTab === "doctor-data" && (
              <div className="coming-soon">
                Coming Soon
              </div>
            )} */}
            {activeTab === "doctor-data" && (
              // <DoctorDataTab
              //   state={doctorState}
              //   filters={doctorFilters}
              //   onFiltersChange={setDoctorFilters}
              // />
              <DoctorDataTab
                state={doctorState}
                filters={doctorFilters}
                onFiltersChange={setDoctorFilters}
                currentPage={page}
                onPageChange={setPage}
                status={doctorStatus}                 // ✅ ADD
                onStatusChange={setDoctorStatus}  
              />
            )}
          {activeTab === "stockist-details" && (
            <StockistDetailsTab
              state={stockistState}
              status={stockistStatus}
              onStatusChange={setStockistStatus}
              currentPage={stockistPage}
              onPageChange={setStockistPage}
              formatSales={formatSales}
              timeFilter={timeFilter}
              setTimeFilter={setTimeFilter}
            />
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function BrandTerritoryDashboard() {
  const location = useLocation();
  const preselect = location.state || {};  // { divisionName, brandName } from pharma dashboard

  useEffect(() => {
    document.body.classList.add("emcure-dashboard-route-scroll");
    return () => document.body.classList.remove("emcure-dashboard-route-scroll");
  }, []);

  const [months, setMonths] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [brands, setBrands] = useState([]);
  const [salesUnit, setSalesUnit] = useState("absolute");
  const formatSales = useSalesFormatter(salesUnit);

  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedDivision, setSelectedDivision] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState(null);

  const [loadingMonths, setLoadingMonths] = useState(true);
  const [loadingDivisions, setLoadingDivisions] = useState(false);
  const [loadingBrands, setLoadingBrands] = useState(false);

  const [monthsError, setMonthsError] = useState("");
  const [divisionsError, setDivisionsError] = useState("");
  const [brandsError, setBrandsError] = useState("");

  const [territoryLoading, setTerritoryLoading] = useState(false);
  const [territoryError, setTerritoryError] = useState("");
  const [territorySummary, setTerritorySummary] = useState({
    totalTerritories: 0,
    goodPerforming: 0,
    deficitTerritories: 0,
    totalSalesOfficers: 0,
    totalStockists: 0,
  });
  const [territories, setTerritories] = useState([]);
  const [territoryTotal, setTerritoryTotal] = useState(0);
  const [territorySearch, setTerritorySearch] = useState("");
  const [territoryPage, setTerritoryPage] = useState(1);
  const [hasApplied, setHasApplied] = useState(false);
  const [selectedTerritory, setSelectedTerritory] = useState(null);
  const { sortConfig: territorySortConfig, toggleSort: toggleTerritorySort, setSortConfig: resetTerritorySort } =
    useSortableData({ key: "sortOrder", direction: "asc", type: "number" });

  useEffect(() => {
    async function loadMonths() {
      setLoadingMonths(true);
      setMonthsError("");
      try {
        const data = await getMonths();
        setMonths(data || []);
        if (data?.length) {
          setSelectedMonth(data[0]);
        }
      } catch (error) {
        
        setMonthsError("Failed to load months.");
      } finally {
        setLoadingMonths(false);
      }
    }

    loadMonths();
  }, []);

  // -----------------------Division------------------
  // useEffect(() => {
  //   async function loadDivisions() {
  //     if (!selectedMonth) return;

  //     setLoadingDivisions(true);
  //     setDivisionsError("");
  //     setDivisions([]);
  //     setSelectedDivision(null);
  //     setBrands([]);
  //     setSelectedBrand(null);

  //     try {
  //       const data = await getDivisions(selectedMonth.id);
  //       setDivisions(data || []);
  //     } catch (error) {
  //       setDivisionsError("Failed to load divisions.");
  //     } finally {
  //       setLoadingDivisions(false);
  //     }
  //   }

  //   loadDivisions();
  // }, [selectedMonth]);

  // useEffect(() => {
  //   if (!hasApplied) return;
  //   handleApply(territoryPage);
  // }, [territoryPage]);
  useEffect(() => {
    if (!hasApplied) return;
    handleApply();
  }, [territoryPage]);
  useEffect(() => {
  async function fetchDivisions(monthId) {
    if (!monthId) return;

    setLoadingDivisions(true);
    setDivisionsError("");
    setDivisions([]);
    setSelectedDivision(null);
    setBrands([]);
    setSelectedBrand(null);

    try {

      const formattedMonth = monthId.replace("-", "");
      const data = await getDivisions(formattedMonth);

      setDivisions(data); // ✅ same as your working frontend

    } catch (error) {
      setDivisionsError("Failed to load divisions.");
    } finally {
      setLoadingDivisions(false);
    }
  }

  // ✅ pass selectedMonth.id like your working code
  if (selectedMonth?.id) {
    fetchDivisions(selectedMonth.id);
  }

  }, [selectedMonth]);

  useEffect(() => {
    async function loadBrands() {
      if (!selectedMonth || !selectedDivision) return;

      setLoadingBrands(true);
      setBrandsError("");
      setBrands([]);
      setSelectedBrand(null);

      try {
        const formattedMonth = selectedMonth.id.replace("-", "");
        const divisionValue = selectedDivision.name;

        const data = await getBrands(formattedMonth, divisionValue);
        setBrands(data || []);
      } catch (error) {
        setBrandsError("Failed to load brands.");
      } finally {
        setLoadingBrands(false);
      }
    }

    loadBrands();
  }, [selectedMonth, selectedDivision]);

  useEffect(() => {
    setHasApplied(false);
    setTerritories([]);
    setSelectedTerritory(null);
    setTerritorySearch("");
    setTerritoryPage(1);
    setTerritoryError("");
    // setTerritorySummary({
    //   totalTerritories: data.total || 0,  // 🔥 FIX
    //   goodPerforming: data.summary?.goodPerforming || 0,
    //   deficitTerritories: data.summary?.deficitTerritories || 0,
    //   totalSalesOfficers: data.summary?.totalSalesOfficers || 0,
    //   totalStockists: data.summary?.totalStockists || 0,
    // });

    // setTerritorySummary(
    //   data.summary || {
    //     totalTerritories: 0,
    //     goodPerforming: 0,
    //     deficitTerritories: 0,
    //     totalSalesOfficers: 0,
    //     totalStockists: 0,
    //   }
    // );
    resetTerritorySort({ key: "sortOrder", direction: "asc", type: "number" });
  }, [selectedMonth, selectedDivision, selectedBrand, resetTerritorySort]);

  async function handleApply(page = territoryPage) {
    if (!selectedMonth || !selectedDivision || !selectedBrand) return;

    setTerritoryLoading(true);
    setTerritoryError("");
    setSelectedTerritory(null);
    // setTerritoryPage(1);

    try {
      const formattedMonth = selectedMonth.id.replace("-", "");
      // const data = await getTerritoryPerformance({
      //   month: formattedMonth,
      //   divisionId: selectedDivision.name,
      //   brandId: selectedBrand.name,
      //   page: territoryPage,
      //   page_size: PAGE_SIZE
      // });

      // setTerritorySummary(
      //   data.summary || {
      //     totalTerritories: 0,
      //     goodPerforming: 0,
      //     deficitTerritories: 0,
      //     totalSalesOfficers: 0,
      //     totalStockists: 0,
      //   }
      // );
      // // setTerritories(data.items || []);
      // setTerritories(data.data || []);
      // setTerritoryTotal(data.total || 0);
      // setHasApplied(true);

      const data = await getTerritoryPerformance({
        month: formattedMonth,
        divisionId: selectedDivision.name,
        brandId: selectedBrand.name,
        // page: territoryPage || pageOverride,
        page: page,
        page_size: PAGE_SIZE
      });

      setTerritorySummary(
        data.summary || {
          totalTerritories: 0,
          goodPerforming: 0,
          deficitTerritories: 0,
          totalSalesOfficers: 0,
          totalStockists: 0,
        }
      );

      // setTerritories(
      //   Array.isArray(data) ? data : data.item || []
      // );
      setTerritories(data.items || []);
      setTerritoryTotal(data.total || 0); // ✅ FIXED
      setHasApplied(true);
    } catch (error) {
      setHasApplied(true);
      setTerritorySummary({
        totalTerritories: 0,
        goodPerforming: 0,
        deficitTerritories: 0,
        totalSalesOfficers: 0,
        totalStockists: 0,
      });
      setTerritories([]);
      setTerritoryError("Failed to load territory performance.");
    } finally {
      setTerritoryLoading(false);
    }
  }

  

  const filteredTerritories = useMemo(() => {
  return sortRows(territories, territorySortConfig);
}, [territories, territorySortConfig]);

  // const territoryTotalPages = Math.max(1, Math.ceil(filteredTerritories.length / PAGE_SIZE));
  // const territoryTotalPages = Math.ceil(territoryTotal / PAGE_SIZE);
  const territoryTotalPages = Math.max(1, Math.ceil(territoryTotal / PAGE_SIZE));
  // const visibleTerritories = filteredTerritories.slice((territoryPage - 1) * PAGE_SIZE, territoryPage * PAGE_SIZE);

  // useEffect(() => {
  //   if (territoryPage > territoryTotalPages) setTerritoryPage(1);
  // }, [territoryPage, territoryTotalPages]);

  useEffect(() => {
    if (!hasApplied) return;

    if (territoryPage > territoryTotalPages && territoryTotalPages > 0) {
      setTerritoryPage(territoryTotalPages);
    }
  }, [territoryTotalPages]);

  return (
    <div className="emcure-brand-dashboard-embed">
    <div className="dashboard-shell">
      <div className="dashboard-topbar-new">
        <div className="brand-lockup-col">
          <div className="brand-title">Emcure</div>
          <div className="brand-subtitle">AI Brand Manager</div>
        </div>
        <DashboardPanelSwitcher direction="down" />
      </div>

      <FiltersBar
        months={months}
        selectedMonth={selectedMonth}
        onMonthChange={setSelectedMonth}
        loadingMonths={loadingMonths}
        monthsError={monthsError}
        divisions={divisions}
        selectedDivision={selectedDivision}
        onDivisionChange={setSelectedDivision}
        loadingDivisions={loadingDivisions}
        divisionsError={divisionsError}
        brands={brands}
        selectedBrand={selectedBrand}
        onBrandChange={setSelectedBrand}
        loadingBrands={loadingBrands}
        brandsError={brandsError}
        // onApply={handleApply}
        onApply={() => {
          setTerritoryPage(1);
          handleApply(1);
        }}
        applyDisabled={!selectedMonth || !selectedDivision || !selectedBrand || loadingDivisions || loadingBrands}
      />

      {hasApplied && (
        <TerritoryTable
          salesUnit={salesUnit} 
          setSalesUnit={setSalesUnit}
          month={selectedMonth}
          division={selectedDivision}
          brand={selectedBrand}
          summary={territorySummary}
          // territories={visibleTerritories}
          territories={territories}
          loading={territoryLoading}
          error={territoryError}
          search={territorySearch}
          onSearchChange={(value) => {
            setTerritoryPage(1);
            setTerritorySearch(value);
          }}
          currentPage={territoryPage}
          totalPages={territoryTotalPages}
          // onPageChange={setTerritoryPage}
          onPageChange={(page) => {
            setTerritoryPage(page);
            handleApply(page);
          }}
          onDrillDown={setSelectedTerritory}
          sortConfig={territorySortConfig}
          onSort={toggleTerritorySort}
        />
      )}

      {selectedTerritory && (
        <TerritoryDrilldownModal
          month={selectedMonth}
          division={selectedDivision}
          brand={selectedBrand}
          territory={selectedTerritory}
          onClose={() => setSelectedTerritory(null)}
          formatSales={formatSales}
        />
      )}
    </div>
    </div>
  );
}
